import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  MoreHorizontal, 
  RefreshCw, 
  RotateCcw, 
  Unplug, 
  Trash2, 
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { format } from 'date-fns';
import { TradingAccountDetails } from '@/services/admin/trading-accounts-service';
import { toast } from '@/hooks/use-toast';
import { BulkActionsDialog } from './BulkActionsDialog';

interface TradingAccountsTableProps {
  accounts: TradingAccountDetails[];
  isLoading: boolean;
  onRefreshConnection: (accountId: string) => Promise<void>;
  onSyncBalance: (accountId: string) => Promise<void>;
  onDisconnectAccount: (accountId: string) => Promise<void>;
  onDeleteAccount: (accountId: string) => Promise<void>;
  onBulkActions: (accountIds: string[], action: string) => Promise<void>;
}

export function TradingAccountsTable({
  accounts,
  isLoading,
  onRefreshConnection,
  onSyncBalance,
  onDisconnectAccount,
  onDeleteAccount,
  onBulkActions
}: TradingAccountsTableProps) {
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [bulkAction, setBulkAction] = useState<'disconnect' | 'delete' | null>(null);
  const [showBulkDialog, setShowBulkDialog] = useState(false);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedAccounts(accounts.map(acc => acc.id));
    } else {
      setSelectedAccounts([]);
    }
  };

  const handleSelectAccount = (accountId: string, checked: boolean) => {
    if (checked) {
      setSelectedAccounts(prev => [...prev, accountId]);
    } else {
      setSelectedAccounts(prev => prev.filter(id => id !== accountId));
    }
  };

  const handleAction = async (action: () => Promise<void>, loadingKey: string) => {
    setActionLoading(loadingKey);
    try {
      await action();
      toast({
        title: "Success",
        description: "Action completed successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkAction = async (action: string) => {
    if (selectedAccounts.length === 0) return;
    
    if (action === 'disconnect' || action === 'delete') {
      setBulkAction(action as 'disconnect' | 'delete');
      setShowBulkDialog(true);
      return;
    }
    
    await handleAction(
      () => onBulkActions(selectedAccounts, action),
      `bulk-${action}`
    );
    setSelectedAccounts([]);
  };

  const handleBulkConfirm = async () => {
    if (!bulkAction || selectedAccounts.length === 0) return;
    
    setShowBulkDialog(false);
    await handleAction(
      () => onBulkActions(selectedAccounts, bulkAction),
      `bulk-${bulkAction}`
    );
    setSelectedAccounts([]);
    setBulkAction(null);
  };

  const getSelectedApiConnectionIds = () => {
    return selectedAccounts.map(accountId => {
      const account = accounts.find(acc => acc.id === accountId);
      return account?.api_connection.id;
    }).filter(Boolean) as string[];
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return <Badge variant="default" className="bg-green-500/10 text-green-500">Connected</Badge>;
      case 'disconnected':
        return <Badge variant="secondary" className="bg-gray-500/10 text-gray-500">Disconnected</Badge>;
      case 'expired':
        return <Badge variant="destructive" className="bg-red-500/10 text-red-500">Expired</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getSyncStatusIcon = (status: string | null) => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <RefreshCw className="h-4 w-4 text-gray-400" />;
    }
  };

  const formatBalance = (balance: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(balance);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Trading Accounts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 bg-muted rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Trading Accounts ({accounts.length})</CardTitle>
          {selectedAccounts.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">
                {selectedAccounts.length} selected
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleBulkAction('refresh')}
                disabled={!!actionLoading}
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Refresh All
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleBulkAction('sync')}
                disabled={!!actionLoading}
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Sync All
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleBulkAction('disconnect')}
                disabled={!!actionLoading}
              >
                <Unplug className="h-4 w-4 mr-1" />
                Disconnect All
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleBulkAction('delete')}
                disabled={!!actionLoading}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete All
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={selectedAccounts.length === accounts.length && accounts.length > 0}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead>Account Number</TableHead>
                <TableHead>Account ID</TableHead>
                <TableHead>Balance</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Connection</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Update</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.map((account) => (
                <TableRow key={account.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedAccounts.includes(account.id)}
                      onCheckedChange={(checked) => handleSelectAccount(account.id, !!checked)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-bold text-lg">{account.number_account_trading}</div>
                      <div className="text-xs text-muted-foreground">Account Number</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-medium font-mono">{account.accountid_trading}</div>
                      <div className="text-xs text-muted-foreground">Trading ID</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-lg">
                      {formatBalance(account.balance_account)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={account.live ? "default" : "secondary"} className={account.live ? "bg-green-500/10 text-green-500" : "bg-gray-500/10 text-gray-500"}>
                      {account.live ? "Live" : "Demo"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-medium">{account.api_connection.csp_account.csp_account_name}</div>
                      <div className="text-sm text-muted-foreground">{account.api_connection.csp_account.user.email}</div>
                      {account.api_connection.csp_account.broker && (
                        <Badge variant="outline" className="text-xs">{account.api_connection.csp_account.broker}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-sm">{account.api_connection.api_name}</div>
                      <div className="text-xs text-muted-foreground font-mono">
                        {account.api_connection.client_id.substring(0, 8)}...
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      {getStatusBadge(account.api_connection.status)}
                      <div className="flex items-center space-x-1">
                        {getSyncStatusIcon(account.api_connection.balance_sync_status)}
                        <span className="text-xs text-muted-foreground">
                          {account.api_connection.balance_sync_status || 'Never'}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-muted-foreground">
                      {account.last_balance_update ? 
                        format(new Date(account.last_balance_update), 'MMM d, HH:mm') :
                        format(new Date(account.updated_at), 'MMM d, yyyy')
                      }
                    </div>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem
                          onClick={() => handleAction(
                            () => onRefreshConnection(account.id),
                            `refresh-${account.id}`
                          )}
                          disabled={actionLoading === `refresh-${account.id}`}
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Refresh Connection
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleAction(
                            () => onSyncBalance(account.id),
                            `sync-${account.id}`
                          )}
                          disabled={actionLoading === `sync-${account.id}`}
                        >
                          <RotateCcw className="h-4 w-4 mr-2" />
                          Sync Balance
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleAction(
                            () => onDisconnectAccount(account.id),
                            `disconnect-${account.id}`
                          )}
                          disabled={actionLoading === `disconnect-${account.id}`}
                        >
                          <Unplug className="h-4 w-4 mr-2" />
                          Disconnect
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleAction(
                            () => onDeleteAccount(account.id),
                            `delete-${account.id}`
                          )}
                          disabled={actionLoading === `delete-${account.id}`}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Account
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <BulkActionsDialog
        open={showBulkDialog}
        onOpenChange={setShowBulkDialog}
        action={bulkAction}
        selectedCount={selectedAccounts.length}
        onConfirm={handleBulkConfirm}
        isLoading={!!actionLoading}
      />
    </Card>
  );
}